/**
 * asbab-nuzul.ts — Occasions of Revelation (Asbāb al-Nuzūl).
 *
 * Primary source: Al-Wāhidī, "Asbāb al-Nuzūl" (d. 468 AH / 1075 CE).
 * English rendering is a public-domain summary translation.
 *
 * Key format: "{surah}:{ayah}" — e.g. "2:185", "96:1".
 * Returns `null` when no occasion is recorded.
 */

export interface AsbabEntry {
  /** Short occasion-of-revelation text in English. */
  text: string;
  /** Source citation for display. */
  source: string;
}

const ASBAB: Record<string, AsbabEntry> = {
  '2:185': {
    text: 'Revealed during Ramadan when the obligation of fasting was established for the believers.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '2:219': {
    text: 'Revealed in response to questions from ʿUmar ibn al-Khaṭṭāb and Muʿādh ibn Jabal about intoxicants and gambling.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '2:256': {
    text: 'Revealed regarding the people of Madīnah who had children who became Jews or Christians before Islam; the verse forbids compelling them to embrace Islam.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '4:11': {
    text: 'Revealed when Jābir ibn ʿAbdullāh asked the Prophet ﷺ about the share of inheritance due to daughters.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '4:43': {
    text: 'Revealed when ʿAlī ibn Abī Ṭālib led prayer while intoxicated before alcohol was prohibited.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '4:101': {
    text: 'Revealed during the expedition of Usfān; the believers feared to shorten their prayers lest the enemy attack.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '5:3': {
    text: 'The final part ("Today I have perfected your religion…") was revealed on the Day of ʿArafah during the Farewell Pilgrimage.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '5:51': {
    text: 'Revealed regarding ʿUbādah ibn al-Ṣāmit who renounced his alliance with the Jews of Madīnah.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '9:40': {
    text: 'Revealed alluding to the migration of the Prophet ﷺ and Abū Bakr to Madīnah when they took shelter in the Cave of Thawr.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '17:85': {
    text: 'Revealed in response to the Jews of Madīnah who asked the Prophet ﷺ about the spirit (al-rūḥ).',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '24:11': {
    text: "Revealed concerning the false accusations (Ḥadīth al-Ifk) made against ʿĀʾisha bint Abī Bakr during the expedition of Banū al-Muṣṭaliq.",
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '33:37': {
    text: "Revealed regarding the Prophet's ﷺ marriage to Zaynab bint Jaḥsh after her divorce from Zayd ibn Ḥārithah.",
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '58:1': {
    text: 'Revealed when Khawlah bint Thaʿlabah complained to the Prophet ﷺ about her husband\'s act of ẓihār (a form of pre-Islamic divorce).',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '80:1': {
    text: 'Revealed when the Prophet ﷺ frowned and turned away from ʿAbdullāh ibn Umm Maktūm, a blind man, while engaged with Quraysh leaders.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '93:1': {
    text: 'Revealed after a period of pause in revelation during which the mushrikūn taunted the Prophet ﷺ, saying his Lord had forsaken him.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '96:1': {
    text: 'The first five verses were the very first revelation received by the Prophet ﷺ in the Cave of Ḥirāʾ through the Angel Jibrīl.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '108:3': {
    text: 'Revealed about al-ʿĀṣ ibn Wāʾil who called the Prophet ﷺ "abtar" (one cut off, without offspring) after the death of his son al-Qāsim.',
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
  '111:1': {
    text: "Revealed about Abū Lahab (ʿAbd al-ʿUzzā ibn ʿAbd al-Muṭṭalib) who cursed the Prophet ﷺ when he was called to invite his kinfolk to Islam.",
    source: 'Al-Wāhidī, Asbāb al-Nuzūl',
  },
};

/**
 * Return the occasion-of-revelation entry for the given verse key,
 * or `null` if none is recorded.
 */
export function getAsbabEntry(verseKey: string): AsbabEntry | null {
  return ASBAB[verseKey] ?? null;
}
